import { useReadAlongFollow } from '@listening/frontend/read-along/use-read-along-follow';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { useCallback, useEffect, useRef } from 'react';
import {
  ChapterHeaderRender,
  LoadingParagraphRender,
  ParagraphRender,
  useSeekDelegationHandlers,
} from '@/components/start/read-along/read-along-renders';
import { useAudioChunkSelector } from '@/lib/audio-chunk-player';
import { HighlightRegistryContext } from '@/lib/doc-reader/highlight/highlight-registry';
import {
  DocReaderContext,
  type DocReaderViewHandle,
  useInitialScroll,
} from '@/lib/doc-reader/core';
import {
  tanstackScrollToIndex,
  useVirtual,
} from '@/lib/doc-reader/core-tanstack';
import {
  createLegacyTextFollowAdapter,
  WEB_TEXT_FOLLOW_BAND,
  webFollowGestureProps,
} from './follow-adapters';
import { useLayoutInvalidation } from './use-layout-invalidation';
import { useElementHeight } from './use-element-height';
import {
  DEFAULT_READER_SCROLL_OPTIONS,
  type ReaderViewProps,
  useReaderCommon,
  useReaderCommonWithScrollFns,
} from './reader-common';
import { VirtualScrollContainer } from './virtual-scroll-container';

// Used by the virtualizer's `initialRect` during SSR / first client render so
// that the rendered range covers every prefetched item rather than just the
// initial viewport. After mount, `observeElementRect` swaps in the real
// scroll-element rect and windowing kicks in normally. Finite (not
// MAX_SAFE_INTEGER) to keep downstream math in safe-integer range.
const SSR_INITIAL_RECT = {
  width: 1200,
  height: 1_000_000,
} as const;

export function ReaderViewTanStack({
  ref,
  ...props
}: ReaderViewProps & {
  ref?: React.RefObject<DocReaderViewHandle | null>;
}) {
  const {
    overscan = DEFAULT_READER_SCROLL_OPTIONS.overscan,
    onIsActiveVisibleChange,
    headerEstimateSize,
    paragraphEstimateSizePerWord,
    scrollContainerClassName,
    scrollContainerParentClassName,
    scrollHeader,
    renders,
  } = props;

  const readerCommon = useReaderCommon({
    headerEstimateSize,
    paragraphEstimateSizePerWord,
    onIsActiveVisibleChange,
    renders,
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollHeaderSize = useElementHeight<HTMLDivElement>();
  const virtualizer = useVirtual({
    overscan,
    getScrollElement: () => parentRef.current,
    count: readerCommon.scrollableObjects.scrollableItemsCount,
    estimateSize: readerCommon.estimateSizeOfScrollableElement,
    scrollMargin: scrollHeaderSize.height,
    // SSR-friendly initial rect: claim a viewport tall enough to render every
    // prefetched item on the server. After mount, observeElementRect measures
    // the real scroll element and the virtualizer collapses to a windowed
    // range. Same value flows to client first render so hydration matches.
    initialRect: SSR_INITIAL_RECT,
  });

  const scrollToIndex = useCallback(
    (index: number) => {
      tanstackScrollToIndex(virtualizer, index);
    },
    [virtualizer],
  );
  const scrollToStart = useCallback(() => {
    parentRef.current?.scrollTo({ top: 0 });
  }, []);

  // Shared read-along follow engine (same machine as the doc-pack readers).
  // Anchor: the active paragraph ELEMENT while mounted; a ttsId pseudo-anchor
  // (coarse virtualizer jump) while it isn't. Created once; latest layout
  // flows through refs.
  const { autoScroll } = useContextAndErrorIfNull(DocReaderContext);
  const ttsId = useAudioChunkSelector((s) => s.ttsId);
  const ttsIdRef = useRef(ttsId);
  ttsIdRef.current = ttsId;
  const activeParagraphElementRef = readerCommon.activeParagraphElementRef;
  const virtualRef = useRef(virtualizer);
  virtualRef.current = virtualizer;
  const { findScrollableIndexOfTtsChunkId } = readerCommon.scrollableObjects;
  const findIndexRef = useRef(findScrollableIndexOfTtsChunkId);
  findIndexRef.current = findScrollableIndexOfTtsChunkId;
  const handleIsActiveVisibleChange = readerCommon.core.isActiveVisibleState[1];
  const visibleChangeRef = useRef(handleIsActiveVisibleChange);
  visibleChangeRef.current = handleIsActiveVisibleChange;
  const adapterRef = useRef<ReturnType<
    typeof createLegacyTextFollowAdapter
  > | null>(null);
  adapterRef.current ??= createLegacyTextFollowAdapter({
    getActiveElement: () => activeParagraphElementRef.current,
    getTtsId: () => ttsIdRef.current,
    findScrollableIndexOfTtsChunk: (chunkId) =>
      findIndexRef.current(chunkId)?.index ?? null,
    getScrollElement: () => parentRef.current,
    getVirtualizer: () => virtualRef.current,
    onActiveVisibleChange: (visible) => {
      visibleChangeRef.current(visible);
    },
  });
  const follow = useReadAlongFollow({
    adapter: adapterRef.current,
    prefEnabled: autoScroll,
    band: WEB_TEXT_FOLLOW_BAND,
  });
  const {
    engage: followEngage,
    disengage: followDisengage,
    layoutInvalidated: followLayoutInvalidated,
    notifyUserScrollStart,
    notifyUserScrollEnd,
  } = follow;

  // Feed every geometry change (window resize, font/spacing prefs) back to the
  // follow engine so a paused viewport doesn't point at stale layout.
  useLayoutInvalidation(followLayoutInvalidated, parentRef, contentRef);

  const gestureEndTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const onUserGesture = useCallback(() => {
    notifyUserScrollStart();
    clearTimeout(gestureEndTimer.current);
    gestureEndTimer.current = setTimeout(notifyUserScrollEnd, 250);
  }, [notifyUserScrollStart, notifyUserScrollEnd]);
  // Disarm the pending gesture-end timer on unmount so it can't fire into a
  // torn-down controller.
  useEffect(() => () => clearTimeout(gestureEndTimer.current), []);

  const { scrollToChunk } = useReaderCommonWithScrollFns({
    readerCommon,
    ref,
    scrollToIndex,
    scrollToStart,
    follow: { engage: followEngage, disengage: followDisengage },
  });

  // const virtualItems = virtualizer.getVirtualItems();

  const virtualItems = virtualizer.virtualItems;
  // If no virtual items, assume only first item is visible (min/max index is 0)
  const minVirtualIndex = virtualItems[0]?.index ?? 0;
  const maxVirtualIndex = virtualItems.at(-1)?.index ?? 0;
  const { virtualItemsChanged } = readerCommon;
  useEffect(() => {
    virtualItemsChanged(minVirtualIndex, maxVirtualIndex);
  }, [maxVirtualIndex, minVirtualIndex, virtualItemsChanged]);

  const scrollableElements = virtualItems.map((virtualRow) => {
    const item = readerCommon.scrollableObjects.getScrollableItemProps(
      virtualRow.index,
    );
    return { virtualRow, item };
  });

  const setActiveParagraphElement = readerCommon.setActiveParagraphElement;

  useInitialScroll({
    core: readerCommon.core,
    scrollToChunk,
    virtualizerHandleRef: {
      current: virtualizer.scrollElement,
    },
  });

  const gestureProps = webFollowGestureProps({
    getScrollElement: () => parentRef.current,
    onUserGesture,
  });
  const seekHandlers = useSeekDelegationHandlers();

  return (
    <HighlightRegistryContext value={readerCommon.highlightRegistry}>
      <VirtualScrollContainer
        scrollAreaProps={{
          ...gestureProps,
          onClick: seekHandlers.onClick,
          onKeyDown: (e) => {
            gestureProps.onKeyDown(e);
            seekHandlers.onKeyDown(e);
          },
        }}
        scrollContainerClassName={scrollContainerClassName}
        scrollContainerParentClassName={scrollContainerParentClassName}
        scrollHeader={scrollHeader}
        scrollHeaderRef={scrollHeaderSize.ref}
        scrollMargin={scrollHeaderSize.height}
        scrollRef={parentRef}
        contentRef={contentRef}
        totalSize={virtualizer.totalSize}
        translateY={virtualItems[0]?.start ?? 0}
      >
        {scrollableElements.map((scrollable) => {
          const { virtualRow, item } = scrollable;
          const ChapterHeader =
            renders?.ChapterHeaderRender ?? ChapterHeaderRender;
          const Paragraph = renders?.ParagraphRender ?? ParagraphRender;

          return (
            <div
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualizer.measureElement}
            >
              {item.type === 'chapter-header' ? (
                <ChapterHeader {...item} />
              ) : item.type === 'paragraph' ? (
                <Paragraph
                  ref={item.isActive ? setActiveParagraphElement : null}
                  {...item}
                />
              ) : (
                <LoadingParagraphRender {...item} />
              )}
            </div>
          );
        })}
      </VirtualScrollContainer>
    </HighlightRegistryContext>
  );
}
